/**
 * Utility functions for the experience strategy
 */

import { runAIMatches, runMultiMatches } from '../../api';
import { EXPERIENCE_LEVELS } from './constants';
import centralDiscoveryQueue from '../../centralDiscoveryQueue';

/**
 * Create experience ranges for the histogram (0-12 months, 12-24 months, etc.)
 * @returns {Array} Array of experience range objects
 */
export const createExperienceRanges = () => {
  const experienceRanges = [];
  for (let i = 0; i < 31; i++) {
    const startMonth = i * 12;
    const endMonth = (i + 1) * 12;
    experienceRanges.push({
      bool_str: `(workexp.overall_experience:${startMonth}-${endMonth})`,
      section_id: `internal_workexp_${i}`,
      section_name: 'Overall Years of Experience',
      section_type: 'Must Have',
      macro_id: `_auto_mm_${Math.random().toString(16).substring(2)}`
    });
  }
  return experienceRanges;
};

/**
 * Extract default experience range from central ICP requirements
 * @param {Object} decision - Decision object that may contain getAllRequirements
 * @returns {Array} Default experience range [min, max]
 */
export const extractDefaultExperienceRange = (decision) => {
  // Default to [2, 6] if no experience requirements found
  const defaultRange = [2, 6];
  
  // Check if we have access to central ICP requirements
  if (!decision || !decision.getAllRequirements) {
    return defaultRange;
  }
  
  try {
    // Get all requirements from central repository
    const allRequirements = decision.getAllRequirements();
    
    // Find experience requirements
    const experienceReq = allRequirements.find(req => 
      req.section_name === "Overall Years of Experience" || 
      (req.bool_str && req.bool_str.includes('workexp.overall_experience'))
    );
    
    if (!experienceReq || !experienceReq.bool_str) {
      return defaultRange;
    }
    
    // Parse the bool_str to extract min and max years
    // Format is typically (workexp.overall_experience:X-Y)
    const match = experienceReq.bool_str.match(/workexp\.overall_experience:(\d+)-(\d+)/);
    if (match && match.length >= 3) {
      const minMonths = parseInt(match[1]);
      const maxMonths = parseInt(match[2]);
      
      // Convert months to years and round to nearest integer
      const minYears = Math.round(minMonths / 12);
      const maxYears = Math.round(maxMonths / 12);
      
      console.log(`Extracted experience range from central ICP: ${minYears}-${maxYears} years`);
      
      return [minYears, maxYears];
    }
  } catch (error) {
    console.error('Error extracting default experience range:', error);
  }
  
  return defaultRange;
};

/**
 * Generate combined experience data including both histogram data and level counts
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @param {Object} decision - Optional decision object that may contain handleRefreshResults
 * @param {boolean} fetchHistogram - Whether to fetch histogram data (default: false)
 * @returns {Object} Object containing AI matches result, histogram data, and level counts
 */
export const generateCombinedExperienceData = async (messages, setMessages, decision = null, fetchHistogram = false) => {
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating combined experience data...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // First get the regular AI matches to extract requirements
  const regularMatches = await runAIMatches(messages);
  if (!regularMatches) {
    throw new Error('Failed to get AI matches');
  }

  // Call setMatchResults if available in the decision object
  if (decision && decision.setMatchResults) {
    console.log('Calling setMatchResults with regularMatches');
    decision.setMatchResults(regularMatches);
  }

  // Update match results in the sidebar if handleRefreshResults is provided
  if (decision && decision.handleRefreshResults) {
    console.log('Updating match results in sidebar with handleRefreshResults');
    decision.handleRefreshResults(messages, regularMatches);
  }
  
  // If we don't need to fetch histogram data, return early
  if (!fetchHistogram) {
    console.log('Skipping histogram data fetch as it was not requested');
    return {
      aiMatchesResult: regularMatches,
      histogramData: null,
      levelCounts: {}
    };
  }
  
  // Extract requirements from regularMatches
  const requirements =
    regularMatches.search_gen_response &&
    regularMatches.search_gen_response.icp_requirements
      ? regularMatches.search_gen_response.icp_requirements
      : [];

  // Push ALL requirements to central discovery queue
  if (requirements.length > 0) {
    console.log('🎯 EXPERIENCE STRATEGY: Pushing requirements to central discovery queue:', requirements);
    
    try {
      for (const req of requirements) {
        if (req.section_id && req.section_name) {
          const queueItem = {
            section_id: req.section_id,
            section_name: req.section_name,
            bool_str: req.bool_str || '',
            section_type: req.section_type || 'Must Have',
            disco_done: false,
            topic: centralDiscoveryQueue.inferTopicFromSectionId(req.section_id)
          };
          
          await centralDiscoveryQueue.addOrUpdateItem(queueItem, true);
        }
      }
      console.log('✅ EXPERIENCE STRATEGY: Successfully pushed requirements to central discovery queue');
    } catch (error) {
      console.error('❌ EXPERIENCE STRATEGY: Error pushing requirements to central discovery queue:', error);
    }
  }
  
  // Create combined experience ranges for both histogram and level counts
  const combinedRanges = [];
  
  // 1. Add histogram ranges (0-12 months, 12-24 months, etc.)
  const histogramRanges = createExperienceRanges();
  combinedRanges.push(...histogramRanges);
  
  // 2. Add experience level ranges (ENTRY, JUNIOR, MID, SENIOR)
  const keyToSectionIdMap = {};
  
  // For each experience level in EXPERIENCE_LEVELS (excluding CUSTOM)
  Object.entries(EXPERIENCE_LEVELS).forEach(([key, { range }]) => {
    if (key !== 'CUSTOM' && range) {
      const [startYear, endYear] = range;
      const startMonth = startYear * 12;
      const endMonth = endYear * 12;
      const sectionId = `experience_level_${key.toLowerCase()}`;
      
      // Store the mapping of key to section_id for later lookup
      keyToSectionIdMap[sectionId] = key;
      
      combinedRanges.push({
        bool_str: `(workexp.overall_experience:${startMonth}-${endMonth})`,
        section_id: sectionId,
        section_name: 'Overall Years of Experience',
        section_type: 'Must Have',
        // Don't set macro_id here, it will be assigned by the API
      });
    }
  });
  
  // Run a single multi matches call with the combined ranges
  console.log('Fetching combined experience data with runMultiMatches');
  console.log(`Total combined ranges: ${combinedRanges.length} (${histogramRanges.length} histogram + ${combinedRanges.length - histogramRanges.length} level ranges)`);
  
  const multiMatchesResult = await runMultiMatches(
    requirements,
    [],
    combinedRanges
  );
  
  // Create a separate histogramData object with only the histogram-related requirements
  let histogramData = null;
  if (multiMatchesResult) {
    // Create a copy of multiMatchesResult with only histogram-related requirements
    histogramData = {
      ...multiMatchesResult,
      target_requirements: multiMatchesResult.target_requirements
        ? multiMatchesResult.target_requirements.slice(0, histogramRanges.length)
        : []
    };
    
    console.log(`Filtered histogram data to include only the first ${histogramRanges.length} requirements`);
  }
  
  // Extract level counts from the combined result
  const levelCounts = {};
  
  if (multiMatchesResult && multiMatchesResult.count_by_queries && multiMatchesResult.target_requirements) {
    // Loop through target_requirements to find the macro_id for each section_id
    multiMatchesResult.target_requirements.forEach(req => {
      const sectionId = req.section_id;
      const macroId = req.macro_id;
      const key = keyToSectionIdMap[sectionId];
      
      if (key && macroId && multiMatchesResult.count_by_queries[macroId] !== undefined) {
        const count = multiMatchesResult.count_by_queries[macroId];
        levelCounts[key] = count;
        console.log(`Added count for ${key}: ${count}`);
      }
    });
  }
  
  // Add default values if no counts were found
  if (Object.keys(levelCounts).length === 0) {
    console.log('No level counts found, adding default values');
    // Create a new object to avoid reference issues
    const defaultCounts = {
      ENTRY: 0,
      JUNIOR: 0,
      MID: 0,
      SENIOR: 0
    };
    
    // If we have any counts from the API, use them
    if (multiMatchesResult && multiMatchesResult.count_by_queries) {
      // Try to extract counts from the raw data
      Object.entries(EXPERIENCE_LEVELS).forEach(([key, { range }]) => {
        if (key !== 'CUSTOM' && range) {
          // Look for any counts that might match this level
          const matchingReq = multiMatchesResult.target_requirements?.find(req => 
            req.section_id.includes(`experience_level_${key.toLowerCase()}`)
          );
          
          if (matchingReq && matchingReq.macro_id && multiMatchesResult.count_by_queries[matchingReq.macro_id]) {
            defaultCounts[key] = multiMatchesResult.count_by_queries[matchingReq.macro_id];
          }
        }
      });
    }
    
    // Use the default counts
    Object.assign(levelCounts, defaultCounts);
  }
  
  console.log('Final levelCounts:', levelCounts);
  
  // Combine the results
  return {
    aiMatchesResult: {
      ...regularMatches,
      experienceHistogram: multiMatchesResult, // Keep the full data in aiMatchesResult
      levelCounts: levelCounts
    },
    histogramData: histogramData, // Only return the histogram portion (first 31 elements)
    levelCounts: levelCounts
  };
};

/**
 * Generate match counts for predefined experience levels
 * This is kept for backward compatibility but now uses the combined function internally
 * @param {Array} requirements - The ICP requirements
 * @returns {Object} Object containing match counts for each experience level
 */
export const generateExperienceLevelCounts = async (requirements) => {
  try {
    // Create a dummy messages array and decision object
    const dummyMessages = [];
    const dummySetMessages = () => {};
    const dummyDecision = null;
    
    // Use the combined function but only return the level counts
    const { levelCounts } = await generateCombinedExperienceData(
      dummyMessages,
      dummySetMessages,
      dummyDecision,
      true // Always fetch histogram data
    );
    
    return levelCounts;
  } catch (error) {
    console.error('Error generating experience level counts:', error);
    return {};
  }
};

/**
 * Generate histogram data for experience ranges
 * This is kept for backward compatibility but now uses the combined function internally
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @param {Object} decision - Optional decision object that may contain handleRefreshResults
 * @param {boolean} fetchHistogram - Whether to fetch histogram data (default: false)
 * @returns {Object} Object containing AI matches result and histogram data
 */
export const generateHistogramData = async (messages, setMessages, decision = null, fetchHistogram = false) => {
  // Use the combined function but only return the histogram data
  const { aiMatchesResult, histogramData } = await generateCombinedExperienceData(
    messages,
    setMessages,
    decision,
    fetchHistogram
  );
  
  return {
    aiMatchesResult,
    histogramData
  };
};
